import { useEffect, useState } from 'react';
import { archiveApi, type ArchiveDrillView, type DrillCopyResultView } from '../../api/client';
import { Button, Card, Chip, LoadError, Loading, Notice, SelectField } from '../../components/ui';
import type { Tone } from '../../lib/format';
import { shortWhen, sizeOf } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Restore drill (1.13.0, Teampace 3123, proposal 114; help: staff-archive):
 * pick a company and a day; stitch-archive reads that day back from Stitch's copy (and the company's
 * own storage, when connected), checks every file's checksum and the rows against what Stitch
 * recorded, and says pass or fail. Read only. Asking for one, and its result, are on the company's
 * Activity. Archive health shows the latest drill.
 */

const STATUS: Record<ArchiveDrillView['status'], { tone: Tone; word: string }> = {
  waiting: { tone: 'neutral', word: 'Waiting' },
  running: { tone: 'neutral', word: 'Reading back…' },
  passed: { tone: 'ok', word: 'Passed' },
  failed: { tone: 'crit', word: 'Failed' },
  error: { tone: 'warn', word: 'Couldn’t run' }
};

const n = (x: number) => x.toLocaleString('en-US');
const dayWords = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

export function RestoreDrillCard() {
  const [customerId, setCustomerId] = useState('');
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const data = useData(() => archiveApi.drills(customerId || undefined), [customerId], { live: false });
  const v = data.data;
  const going = v?.drills.some((d) => d.status === 'waiting' || d.status === 'running') ?? false;

  // While a drill waits or reads back, look again every few seconds.
  useEffect(() => {
    if (!going) return;
    const t = setTimeout(data.reload, 5000);
    return () => clearTimeout(t);
  }, [going, v, data.reload]);

  const run = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await archiveApi.runDrill(customerId, day);
      setOpen(r.drill.id);
      setSaid({ tone: 'ok', text: r.created ? 'Asked. stitch-archive starts within a minute; this card follows it.' : 'A drill of that day is already on its way: it’s below.' });
      data.reload();
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy(false);
    }
  };

  const last = v?.drills.find((d) => d.status !== 'waiting' && d.status !== 'running');
  return (
    <Card title="Restore drill" action={last ? <Chip tone={STATUS[last.status].tone}>{`Last: ${STATUS[last.status].word}`}</Chip> : undefined}>
      <div className="card-body stack">
        <p className="muted small">
          Pick a company and a day. Stitch reads that day back from its archive, and from the company’s own storage when it has one, the way a restore would: every file’s checksum,
          and the rows against what Stitch recorded. It only reads; nothing is changed. The company’s Activity shows it.
        </p>
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Loading the archive’s companies" />
        ) : v.companies.length === 0 ? (
          <Notice tone="neutral">Nothing is archived yet, so there’s nothing to read back.</Notice>
        ) : (
          <>
            <SelectField
              label="Company"
              value={customerId}
              disabled={busy}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setDay('');
              }}
              options={[{ value: '', label: 'Choose a company' }, ...v.companies.map((c) => ({ value: c.id, label: `${c.name} (archived ${c.firstDay} to ${c.lastDay})` }))]}
            />
            <SelectField
              label="Day"
              value={day}
              disabled={busy || !customerId || !v.days}
              onChange={(e) => setDay(e.target.value)}
              options={[
                { value: '', label: customerId ? (v.days ? 'Choose a day' : 'Loading days…') : 'Choose a company first' },
                ...(v.days ?? []).map((d) => ({
                  value: d.day,
                  label: `${dayWords(d.day)}: ${d.stitch === null ? 'not in Stitch’s copy' : `${n(d.stitch)} rows in Stitch’s copy`}${d.company === null ? '' : `, ${n(d.company)} in theirs`}`
                }))
              ]}
              hint="The company’s own days (its time zone). The last 60 with anything archived."
            />
            <span>
              <Button variant="primary" disabled={busy || !customerId || !day} onClick={() => void run()}>
                {busy ? 'Asking…' : 'Read it back and check'}
              </Button>
            </span>
          </>
        )}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
      </div>
      {v && v.drills.length > 0 && (
        <ul className="plain-list blocker-list deploy-day" aria-label="Recent restore drills">
          {v.drills.map((d) => (
            <li key={d.id} className="blocker-row" data-state={d.status}>
              <span className="cell-stack">
                <span>
                  <strong>{d.customerName}</strong>, {dayWords(d.day)}
                </span>
                <small>{d.summary || (d.status === 'waiting' ? 'Waiting for stitch-archive (within a minute).' : 'Reading back…')}</small>
                <small className="muted">
                  Asked {shortWhen(d.createdAt)}
                  {d.requestedByName ? ` by ${d.requestedByName}` : ''}
                  {d.finishedAt ? `; done ${shortWhen(d.finishedAt)}` : ''}
                  {d.files ? `; ${n(d.files)} files, ${sizeOf(d.bytes)}` : ''}
                  {d.copies.length > 0 && (
                    <>
                      {' · '}
                      <button type="button" className="link-btn small" onClick={() => setOpen(open === d.id ? null : d.id)} aria-expanded={open === d.id}>
                        {open === d.id ? 'Hide details' : 'Details'}
                      </button>
                    </>
                  )}
                </small>
                {open === d.id && d.copies.map((c) => <CopyDetails key={c.copy} c={c} />)}
              </span>
              <Chip tone={STATUS[d.status].tone} small>
                {STATUS[d.status].word}
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CopyDetails({ c }: { c: DrillCopyResultView }) {
  return (
    <div className="stack" style={{ marginTop: 8 }}>
      <span>
        <strong>{c.copy === 'stitch' ? 'Stitch’s copy' : 'The company’s own copy'}</strong>{' '}
        <Chip tone={c.ok ? 'ok' : 'crit'} small>
          {c.ok ? 'Passed' : 'Failed'}
        </Chip>
      </span>
      {c.note && <small className="muted">{c.note}</small>}
      <ul className="reg-list">
        {c.tables.map((t) => (
          <li key={t.table}>
            <strong>{t.ok ? 'OK' : 'Failed'}:</strong> {t.table}. {t.said}
          </li>
        ))}
        <li>
          <strong>{c.media.ok ? 'OK' : 'Failed'}:</strong> recordings, voicemail and files. {c.media.said}
        </li>
      </ul>
      {c.problems.length > 0 && (
        <Notice tone="crit" title="What didn’t match">
          <ul className="reg-list">
            {c.problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Notice>
      )}
    </div>
  );
}
